<script setup lang="ts">
import { useCVSectionTranslations } from '~/composables/useCVSectionTranslations';
import { useScrollReveal } from '~/composables/useScrollReveal';
import { isRTLLanguage } from '~/constants/cvLanguages';
import { getLinkDisplayName, getLinkMeta } from '~/constants/linkTypes';
import type { CVData, Education as OldEducation, Experience as OldExperience } from '~/types';
import type { Education as CvEducation, Experience as CvExperience } from '~/types/cv';
import { formatDateOfBirth } from '~/utils/dateHelpers';

/**
 * Third business-web template, built from the site's own Sofax design system:
 * near-black display type, hairline borders, one green accent, pill buttons
 * with the label-swap hover and the offset block behind the portrait.
 *
 * Everything sits inside `.sofax` so the design system applies wherever the
 * template renders — the public card page, the editor preview and the bare
 * `/web-preview` iframe all mount it outside a marketing layout.
 *
 * Motion is opt-in (`animated`). The entrance, the fade-up on scroll and the
 * counters depend on the page actually being scrolled, which never happens in
 * a scaled-down mini preview, the editor or a screenshot — there they would
 * leave sections blank or figures at 0. So only the pages a visitor scrolls
 * (the public card, /demo) turn it on; everywhere else the page is drawn in
 * its finished state. Hover motion and the looping decorations run everywhere.
 */

type TimelineExperience = {
  company?: string;
  title?: string;
  position?: string;
  startDate?: string;
  endDate?: string;
  period?: string;
  description?: string;
}

type TimelineEducation = {
  school: string;
  degree?: string;
  startDate?: string;
  endDate?: string;
  period?: string;
}

const props = defineProps<{
  cvData: CVData;
  experiencesList: Array<TimelineExperience | CvExperience | OldExperience>;
  educationList: Array<TimelineEducation | CvEducation | OldEducation>;
  showQRCode?: boolean;
  /** Entrance, scroll reveal and counters; only for a page that is really scrolled. */
  animated?: boolean;
}>();

const { t } = useI18n()
const route = useRoute()
const { publicCvLinkPath } = usePublicCvLinks()

const rootEl = ref<HTMLElement | null>(null)
useScrollReveal(rootEl)

/**
 * `data-reveal` only when animated: the design system hides every element
 * carrying it until the observer has seen it, which is exactly the blank page
 * the non-animated previews must never show.
 */
function reveal(delay = 0) {
  if (!props.animated) return {}
  return { 'data-reveal': '', style: { '--reveal-delay': `${delay}ms` } }
}

// Section labels follow the CV's own language, not the visitor's UI language.
const cvLanguage = computed(() => props.cvData?.cvLanguage || 'cs')
const { translations: sectionTranslations } = useCVSectionTranslations(cvLanguage)
const isRTL = computed(() => isRTLLanguage(cvLanguage.value))

// Levels, dates and the word for "present" read the way the PDF reads them
const { skillLevel, languageLevel, languagePercent, formatPeriod, formatCertificateDate } = useWebCv(
  () => props.cvData,
  () => ({ present: sectionTranslations.value.present || t('cv.edit.present'), nativeSpeaker: sectionTranslations.value.nativeSpeaker }),
)

const firstName = computed(() => props.cvData?.name?.split(' ')[0] || '')
const surname = computed(() => props.cvData?.name?.split(' ').slice(1).join(' ') || '')
const initial = computed(() => props.cvData?.name?.charAt(0)?.toUpperCase() || 'A')

const contact = computed(() => props.cvData?.contact ?? undefined)

/** The card's own public URL, which is what the QR code encodes. */
const publicCardUrl = computed(() => {
  const id = route.params.id
  if (!id) return ''
  const path = publicCvLinkPath('card', String(id))
  return import.meta.client ? `${window.location.origin}${path}` : path
})

const skills = computed(() => ({
  technical: props.cvData?.technicalSkills ?? [],
  soft: props.cvData?.softSkills ?? [],
}))

const websites = computed(() => (props.cvData?.websites ?? []).filter(site => site?.url))

/** Location, date of birth and driving licence as small hero chips; each is dropped when empty. */
const personalDetails = computed(() => {
  const dateOfBirth = formatDateOfBirth(contact.value?.dateOfBirth, cvLanguage.value)
  return [
    { key: 'location', icon: 'bi bi-geo-alt', label: sectionTranslations.value.location, value: contact.value?.location ?? '' },
    { key: 'dateOfBirth', icon: 'bi bi-calendar3', label: sectionTranslations.value.dateOfBirth, value: dateOfBirth },
    { key: 'drivingLicense', icon: 'bi bi-car-front', label: sectionTranslations.value.drivingLicense, value: props.cvData?.drivingLicense ?? '' },
  ].filter(item => item.value && item.value.trim())
})

const certificates = computed(() => (props.cvData?.certificates ?? []).filter(cert => cert && (cert.name || cert.issuer)))
const hobbies = computed(() => (props.cvData?.hobbies ?? []).filter(hobby => hobby && hobby.trim()))

/** Months since year 0, from `YYYY-MM`, `YYYY-MM-DD` or anything `Date` reads. */
function monthIndex(value?: string): number | null {
  if (!value) return null
  const match = value.match(/^(\d{4})-(\d{1,2})/)
  if (match) return Number(match[1]) * 12 + Number(match[2]) - 1
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.getFullYear() * 12 + date.getMonth()
}

/**
 * Whole years from the first role to today (or to the last one's end, if
 * none is current). Overlapping roles are not added up, so this is the span
 * of the career, which is what "years of experience" means on a CV.
 */
const yearsOfExperience = computed(() => {
  const roles = (props.experiencesList ?? []) as TimelineExperience[]
  const starts = roles.map(role => monthIndex(role.startDate)).filter((m): m is number => m !== null)
  if (!starts.length) return 0
  const now = new Date()
  const current = roles.some(role => role.startDate && !role.endDate)
  const ends = roles.map(role => monthIndex(role.endDate)).filter((m): m is number => m !== null)
  const end = current || !ends.length ? now.getFullYear() * 12 + now.getMonth() : Math.max(...ends)
  return Math.max(0, Math.floor((end - Math.min(...starts)) / 12))
})

/** The role without an end date, for the "currently at" badge on the portrait. */
const currentCompany = computed(() => {
  const roles = (props.experiencesList ?? []) as TimelineExperience[]
  return roles.find(role => role.startDate && !role.endDate && role.company)?.company ?? ''
})

/** The figures in the band under the hero; an empty one is dropped, four at most. */
const stats = computed(() =>
  [
    {
      id: 'years',
      value: yearsOfExperience.value,
      suffix: '+',
      label: sectionTranslations.value.yearsExperience,
      icon: '/sofax/feature-icon-2.svg',
    },
    {
      id: 'experiences',
      value: props.experiencesList?.length ?? 0,
      suffix: '',
      label: sectionTranslations.value.experiences,
      icon: '/sofax/feature-icon-4.svg',
    },
    {
      id: 'skills',
      value: skills.value.technical.length + skills.value.soft.length,
      suffix: '',
      label: sectionTranslations.value.skills,
      icon: '/sofax/service-icon-2.svg',
    },
    {
      id: 'languages',
      value: props.cvData?.languages?.length ?? 0,
      suffix: '',
      label: sectionTranslations.value.languages,
      icon: '/sofax/content-icon-1.svg',
    },
    {
      id: 'degrees',
      value: props.educationList?.length ?? 0,
      suffix: '',
      label: sectionTranslations.value.degrees,
      icon: '/sofax/content-icon-2.svg',
    },
  ].filter(stat => stat.value > 0).slice(0, 4),
)

const statColumns: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
}

/** Education and languages share one band; the links live in the hero. */
const hasSideColumn = computed(() =>
  Boolean(props.educationList?.length || props.cvData?.languages?.length || certificates.value.length || hobbies.value.length),
)

/** The marquee needs a few items or it reads as a stutter rather than a band. */
const marqueeSkills = computed(() => (skills.value.technical.length >= 3 ? skills.value.technical.slice(0, 12) : []))

const getExperienceRole = (exp: TimelineExperience | CvExperience | OldExperience): string =>
  exp?.title ?? exp?.position ?? ''

const isCurrentRole = (exp: TimelineExperience | CvExperience | OldExperience): boolean =>
  Boolean((exp as TimelineExperience).startDate && !(exp as TimelineExperience).endDate)

const indexLabel = (index: number) => String(index + 1).padStart(2, '0')
</script>

<template>
  <div
    v-if="props.cvData"
    ref="rootEl"
    :dir="isRTL ? 'rtl' : 'ltr'"
    class="sofax studio-web relative bg-white dark:bg-gray-950"
    :class="{ 'is-animated': props.animated }"
  >

    <!-- Hero: display name, portrait in the template's offset frame -->
    <section class="relative overflow-hidden">
      <!-- Soft accent glow behind the portrait, the only colour field on the page -->
      <div
        class="pointer-events-none absolute -top-24 right-[-10%] z-0 size-[34rem] rounded-full bg-accent-soft/40 blur-3xl dark:bg-accent/15"
        aria-hidden="true"
      />
      <div class="section-space">
        <div class="shell relative">
          <!--
            Template doodles. All decorative (empty alt, aria-hidden) and on z-0
            rather than a negative z-index, which would put them under the
            section's own background. They only appear where there is room
            beside the content, so they never cover a word.
          -->
          <img
            src="/sofax/content-shape-1.svg"
            alt=""
            aria-hidden="true"
            width="125"
            height="137"
            class="studio-pop pointer-events-none absolute -top-2 left-0 z-0 hidden w-12 select-none lg:block xl:w-16"
            style="--d: 900ms"
          >
          <img
            src="/sofax/hero-1-element-1.svg"
            alt=""
            aria-hidden="true"
            width="311"
            height="243"
            class="pointer-events-none absolute bottom-6 left-0 z-0 hidden w-36 select-none opacity-90 2xl:block"
          >

          <div class="relative z-10 grid items-center gap-14 lg:grid-cols-[1.05fr_.95fr] lg:gap-20">
            <div :class="isRTL ? 'text-right' : ''">
              <!-- A `div`, not a `p`: the design system paints every paragraph
                   body-grey, which would beat the eyebrow's own colour. -->
              <div v-if="props.cvData?.name" class="studio-rise eyebrow mb-6" style="--d: 0ms">
                <span class="relative flex size-2" aria-hidden="true">
                  <span class="absolute inline-flex size-full rounded-full bg-accent opacity-75 motion-safe:animate-ping" />
                  <span class="relative inline-flex size-2 rounded-full bg-accent" />
                </span>
                {{ sectionTranslations.hiIm }}
              </div>

              <h1 class="mb-6">
                <span class="studio-rise block" style="--d: 90ms">{{ firstName }}</span>
                <!-- `!` because `.sofax span` sets `color: inherit` and outranks a plain utility -->
                <span v-if="surname" class="studio-rise relative inline-block !text-accent" style="--d: 180ms">
                  {{ surname }}
                  <!-- Hand-drawn underline, drawn in once the name has landed -->
                  <svg
                    class="studio-underline pointer-events-none absolute left-0 w-full"
                    style="top: 0.47em; height: 0.2em"
                    viewBox="0 0 300 20"
                    preserveAspectRatio="none"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path d="M3 14C60 6 140 3 297 9" stroke="#FFC900" stroke-width="5" stroke-linecap="round" pathLength="1" />
                  </svg>
                </span>
              </h1>

              <!-- `!mb-*`: the design system resets every paragraph to `mb-0` -->
              <p
                v-if="props.cvData?.title"
                class="studio-rise lead"
                :class="personalDetails.length ? '!mb-5' : '!mb-10'"
                style="--d: 280ms"
              >
                {{ props.cvData.title }}
              </p>

              <!-- Personal details: location, date of birth, driving licence -->
              <ul
                v-if="personalDetails.length"
                class="studio-rise mb-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted dark:text-gray-400"
                style="--d: 340ms"
              >
                <li v-for="item in personalDetails" :key="item.key" class="flex items-center gap-2">
                  <i :class="item.icon" class="text-accent" aria-hidden="true" />
                  <span class="sr-only">{{ item.label }}:</span>
                  <span class="!text-body dark:!text-gray-300">{{ item.value }}</span>
                </li>
              </ul>

              <div class="studio-rise flex flex-wrap items-center gap-4" style="--d: 400ms">
                <a v-if="contact?.email" :href="`mailto:${contact.email}`" class="btn btn-accent">
                  <span>{{ sectionTranslations.getInTouch }}</span>
                  <span aria-hidden="true">{{ contact.email }}</span>
                </a>
                <a v-if="contact?.phone" :href="`tel:${contact.phone}`" class="btn btn-outline">
                  <span>{{ sectionTranslations.phone }}</span>
                  <span aria-hidden="true">{{ contact.phone }}</span>
                </a>
              </div>

              <!-- Links from the CV, as the template's tag chips -->
              <ul v-if="websites.length" class="studio-rise mt-8 flex flex-wrap gap-3" style="--d: 480ms">
                <li v-for="site in websites" :key="site.url">
                  <a :href="site.url" target="_blank" rel="noopener noreferrer" class="chip !px-5 !py-1.5 !text-sm">
                    <i :class="getLinkMeta(site).biClass" class="mr-2" aria-hidden="true" />{{ getLinkDisplayName(site) }}
                  </a>
                </li>
              </ul>
            </div>

            <div class="relative mx-auto w-full max-w-sm sm:max-w-md lg:max-w-none">
              <!-- Dotted ring turning slowly behind the frame's top corner -->
              <img
                src="/sofax/hero-circle.svg"
                alt=""
                aria-hidden="true"
                width="494"
                height="494"
                class="rotate-animate pointer-events-none absolute -right-14 -top-14 z-0 hidden w-44 select-none opacity-70 dark:invert sm:block xl:w-52"
              >

              <!-- `img-shadow` draws the template's offset block behind the frame,
                   so no `overflow-hidden` here — it would clip that block away. -->
              <div class="studio-portrait relative z-10" style="--d: 150ms">
                <div v-if="props.cvData?.photoUrl" class="img-shadow">
                  <img
                    :src="props.cvData.photoUrl"
                    :alt="props.cvData?.name"
                    class="aspect-[4/5] w-full rounded-[0.625rem] object-cover"
                  >
                </div>
                <!-- The fill goes on an inner box: the offset block is painted
                     above the frame's own background, so a fill there turns black. -->
                <div v-else class="img-shadow">
                  <div class="flex aspect-[4/5] w-full items-center justify-center rounded-[0.625rem] bg-surface dark:bg-gray-800">
                    <!-- `div`: `.sofax span` would force the initial down to body size. -->
                    <div class="font-title text-9xl font-bold text-ink/15 dark:text-white/20">{{ initial }}</div>
                  </div>
                </div>
              </div>

              <!-- Floating badges: the career in one figure, and where they are now -->
              <div
                v-if="yearsOfExperience > 0"
                class="studio-pop absolute -bottom-6 left-3 z-20 sm:-left-8 lg:-left-12"
                style="--d: 750ms"
              >
                <div class="studio-float flex items-center gap-4 rounded-[0.625rem] bg-white px-5 py-4 shadow-lift dark:bg-gray-800 md:px-6 md:py-5">
                  <!-- `div`s: `.sofax span` forces an inherited font size, which
                       would shrink the figure to body text. -->
                  <div class="font-title text-5xl font-bold leading-none -tracking-[2px] text-ink dark:text-white md:text-6xl">
                    <SofaxCountUp v-if="props.animated" :value="String(yearsOfExperience)" :duration="1600" /><template v-else>{{ yearsOfExperience }}</template><span class="!text-accent">+</span>
                  </div>
                  <div class="max-w-[7rem] text-sm font-semibold leading-snug text-muted dark:text-gray-300">
                    {{ sectionTranslations.yearsExperience }}
                  </div>
                </div>
              </div>

              <div
                v-if="currentCompany"
                class="studio-pop absolute right-3 top-6 z-20 sm:-right-6 lg:-right-8"
                style="--d: 950ms"
              >
                <div class="studio-float studio-float-late flex items-center gap-3 rounded-full bg-ink py-2.5 pl-3 pr-5 text-sm text-white shadow-lift dark:bg-white dark:text-ink">
                  <span class="flex size-7 items-center justify-center rounded-full bg-accent text-white" aria-hidden="true">
                    <i class="bi bi-briefcase-fill text-xs" />
                  </span>
                  <div class="flex flex-col leading-tight">
                    <div class="text-[0.7rem] font-semibold uppercase tracking-wider opacity-70">{{ sectionTranslations.currentlyAt }}</div>
                    <div class="font-title text-sm font-bold">{{ currentCompany }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Figures band: big display numbers, each keyed to the section it counts -->
    <section v-if="stats.length" class="surface-alt relative overflow-hidden border-y border-line dark:border-gray-800">
      <div class="py-14 md:py-20">
        <div class="shell relative">
          <img
            src="/sofax/background-shape-2.svg"
            alt=""
            aria-hidden="true"
            width="174"
            height="171"
            class="up-down-40 pointer-events-none absolute -left-8 top-0 z-0 hidden w-16 select-none opacity-70 xl:block"
          >
          <dl
            class="relative z-10 grid gap-y-12 text-center lg:divide-x lg:divide-line dark:lg:divide-gray-800"
            :class="statColumns[stats.length]"
          >
            <div
              v-for="(stat, index) in stats"
              :key="stat.id"
              v-bind="reveal(index * 110)"
              class="group flex flex-col-reverse items-center px-4"
            >
              <dt class="mt-4 text-sm font-semibold uppercase tracking-[0.12em] text-muted dark:text-gray-400">{{ stat.label }}</dt>
              <dd class="studio-figure font-title font-bold leading-[0.9] text-ink dark:text-white">
                <div class="mb-6 flex justify-center">
                  <SofaxIllustrationTile :src="stat.icon" size="sm" tone="white" interactive />
                </div>
                <SofaxCountUp v-if="props.animated" :value="String(stat.value)" /><template v-else>{{ stat.value }}</template><span v-if="stat.suffix" class="!text-accent">{{ stat.suffix }}</span>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </section>

    <!-- About: heading left, prose right, as on the marketing pages -->
    <section v-if="props.cvData?.aboutMe" id="about" class="relative overflow-hidden bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell relative">
          <img
            src="/sofax/content-circle.svg"
            alt=""
            aria-hidden="true"
            width="300"
            height="300"
            class="rotate-animate pointer-events-none absolute -left-24 top-0 z-0 hidden w-48 select-none opacity-60 xl:block"
          >
          <div class="relative z-10 grid gap-10 lg:grid-cols-3 lg:gap-16">
            <div v-bind="reveal()">
              <h2>{{ sectionTranslations.aboutMe }}</h2>
              <span class="studio-bar mt-6 block h-1 w-16 rounded-full bg-accent" aria-hidden="true" />
            </div>
            <div class="lg:col-span-2" v-bind="reveal(120)">
              <!-- Opening quote mark as a graphic, the prose stays plain text -->
              <div class="-mb-4 font-title text-8xl font-bold leading-none text-accent" aria-hidden="true">“</div>
              <p class="whitespace-pre-line text-xl leading-relaxed text-ink dark:text-gray-200 md:text-2xl md:leading-relaxed">{{ props.cvData.aboutMe }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Experience: a timeline of numbered roles, the current one highlighted -->
    <section v-if="props.experiencesList?.length" id="experience" class="surface-alt relative overflow-hidden">
      <div class="section-space">
        <div class="shell relative">
          <img
            src="/sofax/background-shape-6.svg"
            alt=""
            aria-hidden="true"
            width="174"
            height="171"
            class="up-down-40 pointer-events-none absolute -right-16 top-10 z-0 hidden w-28 select-none opacity-50 xl:block"
          >
          <div class="group relative z-10 mb-12 max-w-3xl md:mb-16" v-bind="reveal()">
            <!-- Wrapped: both the tile and the eyebrow are inline-flex, so side
                 by side they would share one line. -->
            <div class="mb-6">
              <SofaxIllustrationTile src="/sofax/feature-icon-4.svg" size="lg" tone="white" interactive />
            </div>
            <div class="eyebrow mb-5">{{ sectionTranslations.experiences }}</div>
            <h2>{{ sectionTranslations.professionalJourney }}</h2>
            <p class="mt-5 text-lg">{{ sectionTranslations.careerHighlights }}</p>
          </div>

          <ol class="studio-timeline relative z-10 flex flex-col gap-6">
            <li
              v-for="(exp, index) in props.experiencesList"
              :key="index"
              class="relative ps-10 md:ps-16"
              v-bind="reveal(index * 90)"
            >
              <!-- Node on the rail: filled and pulsing for the current role -->
              <span
                class="absolute start-0 top-9 z-10 flex size-[1.625rem] -translate-x-1/2 items-center justify-center rounded-full border-2 border-ink bg-surface rtl:translate-x-1/2 dark:border-white dark:bg-gray-900 md:start-0"
                aria-hidden="true"
              >
                <span class="size-2.5 rounded-full" :class="isCurrentRole(exp) ? 'bg-accent motion-safe:animate-pulse' : 'bg-ink dark:bg-white'" />
              </span>

              <article
                class="group hover-shadow grid gap-6 rounded-[0.625rem] border border-line bg-white p-7 transition-all duration-300 dark:border-gray-700 dark:bg-gray-800 md:grid-cols-[5rem_minmax(0,13rem)_1fr] md:gap-10 md:p-9"
              >
                <!-- Outlined running number: the page's one big graphic figure per role -->
                <div class="studio-index hidden font-title text-7xl font-bold leading-none md:block" aria-hidden="true">
                  {{ indexLabel(index) }}
                </div>
                <div>
                  <span v-if="formatPeriod(exp)" class="tag" :class="isCurrentRole(exp) ? '!bg-accent-soft' : ''">{{ formatPeriod(exp) }}</span>
                  <div v-if="exp.company" class="mt-4 font-title text-lg font-bold text-ink dark:text-white">
                    {{ exp.company }}
                  </div>
                </div>
                <div>
                  <h3 class="mb-3">{{ getExperienceRole(exp) }}</h3>
                  <p v-if="exp.description" class="whitespace-pre-line text-base">{{ exp.description }}</p>
                </div>
              </article>
            </li>
          </ol>
        </div>
      </div>
    </section>

    <!-- The template's dark marquee, running the technical skills past -->
    <SofaxTextSlider v-if="marqueeSkills.length" :items="marqueeSkills" />

    <!-- Skills: the template's chips, split into the two groups -->
    <section v-if="skills.technical.length || skills.soft.length" id="skills" class="relative overflow-hidden bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell relative">
          <img
            src="/sofax/hero-1-element-2.svg"
            alt=""
            aria-hidden="true"
            width="291"
            height="200"
            class="pointer-events-none absolute -right-6 top-4 z-0 hidden w-28 select-none xl:block"
          >
          <div class="relative z-10 mb-12 max-w-3xl md:mb-16" v-bind="reveal()">
            <h2>{{ sectionTranslations.skills }}</h2>
            <p class="mt-5 text-lg">{{ sectionTranslations.technologiesDescription }}</p>
          </div>

          <div class="relative z-10 grid gap-8 md:grid-cols-2">
            <div v-if="skills.technical.length" class="group card h-full md:!p-10" v-bind="reveal(80)">
              <div class="mb-8 flex items-center gap-5">
                <SofaxIllustrationTile src="/sofax/service-icon-2.svg" interactive />
                <div class="display-heading display-heading-4">{{ sectionTranslations.technicalSkills }}</div>
              </div>
              <ul class="flex flex-wrap gap-3">
                <li v-for="skill in skills.technical" :key="skill" class="chip inline-flex items-center gap-2.5 !px-5 !py-2 !text-sm">
                  {{ skill }}<CVLevelDots :level="skillLevel(skill)" class="!text-accent" />
                </li>
              </ul>
            </div>

            <div v-if="skills.soft.length" class="group card h-full md:!p-10" v-bind="reveal(180)">
              <div class="mb-8 flex items-center gap-5">
                <SofaxIllustrationTile src="/sofax/contact-icon-1.svg" interactive />
                <div class="display-heading display-heading-4">{{ sectionTranslations.softSkills }}</div>
              </div>
              <ul class="flex flex-wrap gap-3">
                <li v-for="skill in skills.soft" :key="skill" class="chip inline-flex items-center gap-2.5 !px-5 !py-2 !text-sm">
                  {{ skill }}<CVLevelDots :level="skillLevel(skill)" class="!text-accent" />
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Education, languages, certificates and interests: hairline-separated columns -->
    <section v-if="hasSideColumn" class="surface-alt relative overflow-hidden">
      <div class="section-space">
        <div class="shell relative">
          <img
            src="/sofax/hero-2-element-3.svg"
            alt=""
            aria-hidden="true"
            width="311"
            height="243"
            class="pointer-events-none absolute -right-10 top-2 z-0 hidden w-24 select-none opacity-70 xl:block"
          >
          <div class="relative z-10 grid gap-12 md:grid-cols-2 lg:gap-20">
            <div v-if="props.educationList?.length" class="group" v-bind="reveal()">
              <div class="mb-8 flex items-center gap-4">
                <SofaxIllustrationTile src="/sofax/content-icon-2.svg" size="sm" tone="white" interactive />
                <h3>{{ sectionTranslations.education }}</h3>
              </div>
              <ul class="flex flex-col gap-6">
                <li v-for="(edu, index) in props.educationList" :key="index" class="border-b border-line pb-6 last:border-0 last:pb-0 dark:border-gray-800">
                  <div class="font-title text-lg font-bold text-ink dark:text-white">{{ edu.degree }}</div>
                  <p class="mt-1 text-base">{{ edu.school }}</p>
                  <p v-if="formatPeriod(edu)" class="mt-1 text-sm text-muted dark:text-gray-400">{{ formatPeriod(edu) }}</p>
                </li>
              </ul>
            </div>

            <div v-if="props.cvData?.languages?.length" class="group" v-bind="reveal(120)">
              <div class="mb-8 flex items-center gap-4">
                <SofaxIllustrationTile src="/sofax/content-icon-1.svg" size="sm" tone="white" interactive />
                <h3>{{ sectionTranslations.languages }}</h3>
              </div>
              <ul class="flex flex-col gap-6">
                <li v-for="lang in props.cvData.languages" :key="lang.name">
                  <div class="flex items-baseline justify-between gap-4">
                    <span class="font-title text-lg font-bold text-ink dark:text-white">{{ lang.name }}</span>
                    <span class="text-sm text-muted dark:text-gray-400">{{ languageLevel(lang.level) }}</span>
                  </div>
                  <!-- With levels on, and only for a level we can read; anything else keeps its text alone -->
                  <div
                    v-if="languagePercent(lang.level) > 0"
                    class="mt-3 h-2 overflow-hidden rounded-full bg-white dark:bg-gray-800"
                    aria-hidden="true"
                  >
                    <div class="studio-meter h-full rounded-full bg-accent" :style="{ width: `${languagePercent(lang.level)}%` }" />
                  </div>
                </li>
              </ul>
            </div>

            <div v-if="certificates.length" class="group" v-bind="reveal()">
              <div class="mb-8 flex items-center gap-4">
                <SofaxIllustrationTile src="/sofax/service-icon-2.svg" size="sm" tone="white" interactive />
                <h3>{{ sectionTranslations.certificates }}</h3>
              </div>
              <ul class="flex flex-col gap-4">
                <li
                  v-for="(cert, index) in certificates"
                  :key="index"
                  class="border-b border-line pb-4 last:border-0 last:pb-0 dark:border-gray-800"
                >
                  <a
                    v-if="cert.url"
                    :href="cert.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="font-title font-semibold text-ink hover:text-accent dark:text-white"
                  >{{ cert.name }}</a>
                  <span v-else class="font-title font-semibold text-ink dark:text-white">{{ cert.name }}</span>
                  <p v-if="cert.issuer || cert.date" class="mt-1 text-sm text-muted dark:text-gray-400">
                    {{ [cert.issuer, formatCertificateDate(cert.date)].filter(Boolean).join(' · ') }}
                  </p>
                </li>
              </ul>
            </div>

            <div v-if="hobbies.length" class="group" v-bind="reveal(120)">
              <div class="mb-8 flex items-center gap-4">
                <SofaxIllustrationTile src="/sofax/service-icon-3.svg" size="sm" tone="white" interactive />
                <h3>{{ sectionTranslations.hobbies }}</h3>
              </div>
              <ul class="flex flex-wrap gap-3">
                <li v-for="(hobby, index) in hobbies" :key="index">
                  <span class="chip !px-5 !py-1.5 !text-sm">{{ hobby }}</span>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </div>
    </section>

    <!-- Custom sections: card grid with the template's hover offset -->
    <section
      v-for="(section, sectionIndex) in props.cvData?.customSections"
      v-show="section.sectionTitle && section.items?.length"
      :id="'custom-section-' + sectionIndex"
      :key="'custom-' + sectionIndex"
      class="relative overflow-hidden bg-white dark:bg-gray-950"
    >
      <div class="section-space">
        <div class="shell relative">
          <img
            src="/sofax/hero-2-element-1.svg"
            alt=""
            aria-hidden="true"
            width="291"
            height="200"
            class="pointer-events-none absolute -right-8 top-6 z-0 hidden w-28 select-none opacity-80 xl:block"
          >
          <div class="relative z-10 mb-12 max-w-3xl md:mb-16" v-bind="reveal()">
            <h2>{{ section.sectionTitle }}</h2>
            <span class="studio-bar mt-6 block h-1 w-16 rounded-full bg-accent" aria-hidden="true" />
          </div>

          <ul class="relative z-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <li v-for="(item, index) in section.items" :key="index" v-bind="reveal((index % 3) * 100)">
              <article class="hover-shadow h-full overflow-hidden rounded-[0.625rem] border border-line bg-white transition-all duration-300 dark:border-gray-700 dark:bg-gray-800">
                <img v-if="item.image" :src="item.image" :alt="item.title" class="aspect-video w-full object-cover" loading="lazy">
                <div class="p-7">
                  <a
                    v-if="item.url"
                    :href="item.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="display-heading display-heading-5 mb-3 block hover:text-accent"
                  >
                    {{ item.title }}
                  </a>
                  <div v-else class="display-heading display-heading-5 mb-3">{{ item.title }}</div>
                  <p v-if="item.text" class="text-base">{{ item.text }}</p>
                </div>
              </article>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Contact: the template's dark content block -->
    <section id="contact" class="relative overflow-hidden bg-ink dark:bg-gray-900">
      <!-- Faint grid and a green glow give the dark block some depth -->
      <div class="dot-grid pointer-events-none absolute inset-0 z-0 text-white/[0.07]" aria-hidden="true" />
      <div
        class="pointer-events-none absolute -bottom-40 left-1/2 z-0 size-[36rem] -translate-x-1/2 rounded-full bg-accent/25 blur-3xl"
        aria-hidden="true"
      />
      <div class="section-space">
        <div class="shell relative">
          <!-- The star is the one decorative element the template uses on its
               dark blocks, where the line-art doodles would disappear. -->
          <img
            src="/sofax/text-star-white.svg"
            alt=""
            aria-hidden="true"
            width="60"
            height="60"
            class="up-down-40 pointer-events-none absolute left-2 top-4 z-0 hidden w-10 select-none opacity-40 lg:block"
          >
          <img
            src="/sofax/text-star-white.svg"
            alt=""
            aria-hidden="true"
            width="60"
            height="60"
            class="studio-twinkle pointer-events-none absolute right-6 top-24 z-0 hidden w-16 select-none opacity-25 lg:block"
          >
          <img
            src="/sofax/text-star-white.svg"
            alt=""
            aria-hidden="true"
            width="60"
            height="60"
            class="studio-twinkle pointer-events-none absolute bottom-10 left-1/4 z-0 hidden w-6 select-none opacity-30 xl:block"
            style="animation-delay: 1.4s"
          >
          <div class="relative z-10 mx-auto mb-12 max-w-2xl text-center md:mb-16" v-bind="reveal()">
            <h2 class="!text-white">{{ sectionTranslations.letsConnect }}</h2>
            <p class="mt-5 text-lg !text-white/75">{{ sectionTranslations.openToOpportunities }}</p>
          </div>

          <ul class="relative z-10 mx-auto grid max-w-4xl gap-5 sm:grid-cols-2 md:grid-cols-3">
            <li v-if="contact?.email" v-bind="reveal(0)">
              <a
                :href="`mailto:${contact.email}`"
                class="group flex h-full flex-col items-center gap-4 rounded-[0.625rem] border border-white/15 bg-white/[0.02] p-7 text-center backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-white/40 hover:bg-white/5"
              >
                <SofaxIllustrationTile src="/sofax/contact-icon-3.svg" size="sm" tone="white" interactive />
                <span class="font-title font-bold !text-white">{{ sectionTranslations.email }}</span>
                <span class="break-all text-sm !text-white/70">{{ contact.email }}</span>
              </a>
            </li>
            <li v-if="contact?.phone" v-bind="reveal(90)">
              <a
                :href="`tel:${contact.phone}`"
                class="group flex h-full flex-col items-center gap-4 rounded-[0.625rem] border border-white/15 bg-white/[0.02] p-7 text-center backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-white/40 hover:bg-white/5"
              >
                <SofaxIllustrationTile src="/sofax/contact-icon-2.svg" size="sm" tone="white" interactive />
                <span class="font-title font-bold !text-white">{{ sectionTranslations.phone }}</span>
                <span class="text-sm !text-white/70">{{ contact.phone }}</span>
              </a>
            </li>
            <li v-if="contact?.website" v-bind="reveal(180)">
              <a
                :href="contact.website"
                target="_blank"
                rel="noopener noreferrer"
                class="group flex h-full flex-col items-center gap-4 rounded-[0.625rem] border border-white/15 bg-white/[0.02] p-7 text-center backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-white/40 hover:bg-white/5"
              >
                <SofaxIllustrationTile src="/sofax/service-icon-1.svg" size="sm" tone="white" interactive />
                <span class="font-title font-bold !text-white">{{ sectionTranslations.websites }}</span>
                <span class="break-all text-sm !text-white/70">{{ contact.website }}</span>
              </a>
            </li>
            <li v-if="contact?.linkedin" v-bind="reveal(270)">
              <a
                :href="contact.linkedin"
                target="_blank"
                rel="noopener noreferrer"
                class="group flex h-full flex-col items-center gap-4 rounded-[0.625rem] border border-white/15 bg-white/[0.02] p-7 text-center backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-white/40 hover:bg-white/5"
              >
                <span class="flex size-12 items-center justify-center rounded-full bg-white/10 text-2xl !text-white" aria-hidden="true">
                  <i class="bi bi-linkedin" />
                </span>
                <span class="font-title font-bold !text-white">{{ sectionTranslations.linkedin }}</span>
                <span class="break-all text-sm !text-white/70">{{ contact.linkedin }}</span>
              </a>
            </li>
            <li v-if="contact?.location" v-bind="reveal(360)">
              <div class="flex h-full flex-col items-center gap-4 rounded-[0.625rem] border border-white/15 bg-white/[0.02] p-7 text-center">
                <span class="flex size-12 items-center justify-center rounded-full bg-white/10 text-2xl !text-white" aria-hidden="true">
                  <i class="bi bi-geo-alt" />
                </span>
                <span class="font-title font-bold !text-white">{{ sectionTranslations.location }}</span>
                <span class="text-sm !text-white/70">{{ contact.location }}</span>
              </div>
            </li>
          </ul>

          <div v-if="showQRCode && publicCardUrl" class="relative z-10 mt-12 text-center md:mt-16" v-bind="reveal(120)">
            <div class="inline-block rounded-[0.625rem] bg-white p-7 shadow-lift">
              <p class="!mb-4 text-sm font-semibold !text-ink">{{ sectionTranslations.scanToSaveContact }}</p>
              <CommonQRCode :link="publicCardUrl" :size="180" background-color="#FFFFFF" foreground-color="#0E0E0E" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <footer class="border-t border-line bg-white py-10 dark:border-gray-800 dark:bg-gray-950">
      <div class="shell flex flex-col items-center gap-3 text-center">
        <span class="text-sm text-muted dark:text-gray-400">{{ sectionTranslations.createdWith }}</span>
        <a href="https://www.inmyresume.com" target="_blank" rel="noopener noreferrer">
          <img src="/logo.svg" alt="InMyResume" width="264" height="56" class="h-7 w-auto dark:hidden">
          <img src="/logo_white.svg" alt="InMyResume" width="264" height="56" class="hidden h-7 w-auto dark:block">
        </a>
      </div>
    </footer>
  </div>

  <!-- No data state -->
  <div v-else class="sofax flex min-h-screen items-center justify-center bg-white dark:bg-gray-950">
    <div class="shell text-center">
      <SofaxIconTile icon="bi-exclamation-circle" class="mb-6" />
      <h3 class="mb-3">{{ sectionTranslations.noDataAvailable }}</h3>
      <p>{{ sectionTranslations.noDataDescription }}</p>
    </div>
  </div>
</template>

<style scoped>
/*
 * The figures: display size, one step past the h1 on large screens, so the
 * band reads as numbers first and labels second.
 */
.studio-figure {
  font-size: 4rem;
  letter-spacing: -2px;
}

@media (min-width: 768px) {
  .studio-figure {
    font-size: 5.5rem;
    letter-spacing: -3px;
  }
}

@media (min-width: 1280px) {
  .studio-figure {
    font-size: 6.5rem;
  }
}

/* Outlined running number on each role; fills with the accent on hover. */
.studio-index {
  color: transparent;
  -webkit-text-stroke: 1.5px #0e0e0e;
  opacity: 0.18;
  transition: color 300ms ease, opacity 300ms ease, -webkit-text-stroke-color 300ms ease;
}

.group:hover .studio-index {
  color: #0c9347;
  -webkit-text-stroke-color: #0c9347;
  opacity: 1;
}

:where(.dark-mode) .studio-index {
  -webkit-text-stroke-color: #fff;
  opacity: 0.25;
}

/* The rail the timeline nodes sit on. */
.studio-timeline::before {
  content: '';
  position: absolute;
  inset-block: 2.25rem 2.25rem;
  inset-inline-start: 0;
  width: 2px;
  background: linear-gradient(to bottom, #0e0e0e 0%, #eaedf0 100%);
}

:where(.dark-mode) .studio-timeline::before {
  background: linear-gradient(to bottom, #fff 0%, #1f2937 100%);
}

/* Continuous, gentle drift for the portrait badges; runs everywhere. */
@media (prefers-reduced-motion: no-preference) {
  .studio-float {
    animation: studio-float 6s ease-in-out infinite alternate;
  }

  .studio-float-late {
    animation-delay: -3s;
  }

  .studio-twinkle {
    animation: studio-twinkle 3.2s ease-in-out infinite;
  }
}

@keyframes studio-float {
  from {
    transform: translateY(-6px);
  }

  to {
    transform: translateY(6px);
  }
}

@keyframes studio-twinkle {
  0%,
  100% {
    transform: scale(1) rotate(0deg);
  }

  50% {
    transform: scale(0.7) rotate(20deg);
  }
}

/*
 * Entrance, only when the page is animated. Without `is-animated` every class
 * below is inert and the hero is simply there, finished.
 */
@media (prefers-reduced-motion: no-preference) {
  .is-animated .studio-rise {
    animation: studio-rise 900ms cubic-bezier(0.22, 1, 0.36, 1) var(--d, 0ms) both;
  }

  .is-animated .studio-pop {
    animation: studio-pop 700ms cubic-bezier(0.34, 1.56, 0.64, 1) var(--d, 0ms) both;
  }

  .is-animated .studio-portrait {
    animation: studio-portrait 1100ms cubic-bezier(0.22, 1, 0.36, 1) var(--d, 0ms) both;
  }

  .is-animated .studio-underline path {
    stroke-dasharray: 1;
    animation: studio-draw 900ms cubic-bezier(0.65, 0, 0.35, 1) 700ms both;
  }

  /* Revealed bands: the accent bar grows and the language meters fill in. */
  .is-animated [data-reveal] .studio-bar {
    transform-origin: left;
    transform: scaleX(0);
    transition: transform 800ms cubic-bezier(0.22, 1, 0.36, 1) 300ms;
  }

  .is-animated [data-reveal].is-revealed .studio-bar {
    transform: none;
  }

  .is-animated [data-reveal] .studio-meter {
    transform-origin: left;
    transform: scaleX(0);
    transition: transform 1200ms cubic-bezier(0.22, 1, 0.36, 1) 250ms;
  }

  .is-animated [data-reveal].is-revealed .studio-meter {
    transform: none;
  }

  [dir='rtl'] .studio-bar,
  [dir='rtl'] .studio-meter {
    transform-origin: right;
  }
}

@keyframes studio-rise {
  from {
    opacity: 0;
    transform: translateY(1.75rem);
  }
}

@keyframes studio-pop {
  from {
    opacity: 0;
    transform: scale(0.6);
  }
}

@keyframes studio-portrait {
  from {
    opacity: 0;
    clip-path: inset(100% 0 0 0 round 0.625rem);
    transform: translateY(2rem) scale(1.04);
  }

  to {
    clip-path: inset(-2rem -2rem -2rem -2rem);
  }
}

@keyframes studio-draw {
  from {
    stroke-dashoffset: 1;
  }

  to {
    stroke-dashoffset: 0;
  }
}
</style>
